import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react'
import { toast } from 'sonner'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { FROZEN_CROP_MESSAGE } from '@/lib/editor/freeze-object-messages'
import {
  isShapeFrozenForGeometry,
  isShapeLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Crop mode's session: entering it for a photo-filled shape (refusing locked/frozen ones), and
 * the mode's lifecycle effects — the undo floor, and exiting when a shape tool is picked.
 */
export function useCropSession({
  internalShapeManagerRef,
  selectedShape,
  getUndoStackSize,
  cropShapeId,
  setCropShapeId,
  setCropShapeData,
  cropShapeIdRef,
  cropOriginalShapeRef,
  cropGestureBeforeRef,
  cropUndoFloorRef,
  exitCropMode,
  pulseSelectionGeometryBlocked,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  selectedShape: CenterCanvasConfig['selectedShape']
  getUndoStackSize: CenterCanvasConfig['getUndoStackSize']
  cropShapeId: string | null
  setCropShapeId: Dispatch<SetStateAction<string | null>>
  setCropShapeData: Dispatch<SetStateAction<ShapeData | null>>
  cropShapeIdRef: MutableRefObject<string | null>
  cropOriginalShapeRef: MutableRefObject<ShapeData | null>
  cropGestureBeforeRef: MutableRefObject<ShapeData | null>
  cropUndoFloorRef: MutableRefObject<number | null>
  exitCropMode: () => void
  pulseSelectionGeometryBlocked: () => void
}) {
  useEffect(() => {
    cropUndoFloorRef.current = cropShapeId != null ? (getUndoStackSize?.() ?? null) : null
  }, [cropShapeId, getUndoStackSize, cropUndoFloorRef])

  // Exit crop mode when the user picks a shape tool from the panel (same signal node-edit
  // mode reacts to in `use-node-edit-session` — intent to draw a new shape, not keep cropping
  // this one).
  useEffect(() => {
    if (selectedShape != null && cropShapeIdRef.current != null) {
      exitCropMode()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShape])

  /**
   * Enters crop mode for a photo-filled shape — the "Crop Photo" context menu action.
   * Returns false (and leaves crop mode untouched) for a locked/frozen shape or a shape with
   * no photo fill to crop.
   */
  const tryEnterCropMode = useCallback(
    (shape: ShapeData): boolean => {
      if (!shape.photoFill) return false

      const mgr = internalShapeManagerRef.current
      const shapesById = new Map((mgr?.getShapes() ?? []).map((s) => [s.id, s]))

      if (isShapeLockedForUserEdit(shape, shapesById)) return false

      if (isShapeFrozenForGeometry(shape, shapesById)) {
        pulseSelectionGeometryBlocked()
        toast.error(FROZEN_CROP_MESSAGE)
        return false
      }

      setCropShapeId(shape.id)
      setCropShapeData(shape)
      cropOriginalShapeRef.current = shape
      // Guards against a gesture abandoned mid-drag in a previous crop-mode session (e.g.
      // Escape pressed while a handle was held) leaking its stale snapshot into this one.
      cropGestureBeforeRef.current = null

      return true
    },
    [
      pulseSelectionGeometryBlocked,
      internalShapeManagerRef,
      setCropShapeId,
      setCropShapeData,
      cropOriginalShapeRef,
      cropGestureBeforeRef,
    ]
  )

  return { tryEnterCropMode }
}
